/**
 * 本地文件上传到画布节点：格式/大小判定 + 走哪条通道。
 *
 * ## 为什么单开一个模块
 *
 * 上传有**两个入口**（粘贴到画布、点节点卡片上的上传按钮），两条入口必须用
 * 同一套判据——否则会出现「粘贴说格式不支持、点按钮却能传」这种自相矛盾。
 * 判定逻辑放这里，两个入口都引它。
 *
 * ## ⚠️ 三类文件走两条不同的通道，别照抄
 *
 *   图片 / 音频 → base64（JSON body）
 *   视频       → **raw 二进制**（`express.raw`）
 *
 * 视频不能走 base64：单文件上限 50MB，base64 膨胀 33% 后约 67MB，
 * 直接顶爆请求体（Nginx 配的是 64m）。后端 upload-video 也是按 raw 收的。
 * 这条在 CLAUDE.md 里点过名：「与图片/音频上传（都用 base64）不同，别照着它们改回 base64」。
 *
 * ## 限制为什么写在前端
 *
 * 后端有权威校验（ffprobe 读真实元数据），这里这份是**为了早点告诉用户**——
 * 传一个 200MB 的文件要等上传完才被拒，白等几分钟。所以前端先拦一道，
 * 但**不把前端当准**：后端拒了照样透传它的理由。
 */

// 每边 300~6000px、面积、帧率这些要读到文件内容才知道，前端不做——
// 那些交给后端 ffprobe。这里只管"一眼能看出来"的两项：类型和大小。
export const UPLOAD_LIMITS = {
  image: {
    // 前端会先压到 ≤1500px 再传，所以原图放宽一些，挡住的是明显异常的大文件
    maxBytes: 30 * 1024 * 1024,
    mimePrefix: 'image/',
    accept: 'image/*',
    label: '图片',
    hint: 'JPG / PNG / WebP 等，30MB 以内',
  },
  video: {
    // 与后端 VIDEO_REF_LIMITS.maxBytes 同值。改一处必须两处一起改。
    maxBytes: 50 * 1024 * 1024,
    mimePrefix: 'video/',
    // 官方只收 mp4 / mov，别写 video/* ——让用户选完 avi 再被拒是白跑一趟
    accept: 'video/mp4,video/quicktime',
    allowedMimes: ['video/mp4', 'video/quicktime'],
    label: '视频',
    hint: 'MP4 / MOV，50MB 以内',
  },
  audio: {
    maxBytes: 10 * 1024 * 1024,
    mimePrefix: 'audio/',
    accept: 'audio/wav,audio/mpeg,audio/ogg,audio/opus,audio/x-wav',
    allowedMimes: ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp3', 'audio/ogg', 'audio/opus'],
    label: '音频',
    hint: 'WAV / MP3 / OGG / Opus，10MB 以内',
  },
};

/** 这个文件该落到哪种节点。认不出回 null——**不猜**，猜错会建错节点类型。 */
export function detectUploadType(file) {
  const mime = String(file?.type || '').toLowerCase();
  if (!mime) return null;
  for (const [rawType, limit] of Object.entries(UPLOAD_LIMITS)) {
    if (mime.startsWith(limit.mimePrefix)) return rawType;
  }
  return null;
}

/**
 * 能不能传。
 * @returns {{ok: true, rawType}} 或 {{ok: false, reason}} —— reason 是给用户看的人话
 */
export function checkUploadFile(file) {
  if (!file) return { ok: false, reason: '没有拿到文件' };
  const rawType = detectUploadType(file);
  if (!rawType) {
    return { ok: false, reason: `不支持这种文件（${file.type || '未知格式'}），只能传图片、视频或音频` };
  }
  const limit = UPLOAD_LIMITS[rawType];
  if (limit.allowedMimes && !limit.allowedMimes.includes(String(file.type).toLowerCase())) {
    return { ok: false, reason: `${limit.label}只支持 ${limit.hint.split('，')[0]}，这个是 ${file.type}` };
  }
  if (file.size > limit.maxBytes) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    const max = Math.round(limit.maxBytes / 1024 / 1024);
    return { ok: false, reason: `${limit.label} ${mb}MB，超过 ${max}MB 上限` };
  }
  return { ok: true, rawType };
}

/** 从剪贴板事件里挑出所有能传的文件。粘贴纯文本时返回空数组，调用方据此回退到"粘贴文本"。 */
export function filesFromClipboard(event) {
  const items = Array.from(event?.clipboardData?.items || []);
  return items
    .filter(item => item.kind === 'file')
    .map(item => item.getAsFile())
    .filter(Boolean);
}

/**
 * 图片压到长边 ≤ maxSide 再传。
 *
 * 不是为了省流量——Nginx 的 body 上限就在那儿，而画布上一张参考图根本用不到
 * 原图分辨率。老画布也是这么做的（CLAUDE.md：「本地图上传前端先压到 ≤1500px」）。
 * 压不动（浏览器不支持 / 图坏了）就原样返回，让后端去判。
 */
export function compressImage(file, maxSide = 1500) {
  return new Promise(resolve => {
    if (typeof window === 'undefined' || !window.FileReader || !document?.createElement) {
      resolve(file);
      return;
    }
    const image = new Image();
    const url = URL.createObjectURL(file);
    const done = (value) => { URL.revokeObjectURL(url); resolve(value); };
    image.onerror = () => done(file);
    image.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
      if (scale >= 1) { done(file); return; }
      try {
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => done(blob || file), 'image/jpeg', 0.92);
      } catch { done(file); }
    };
    image.src = url;
  });
}

export function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('读取文件失败'));
    // data:image/jpeg;base64,xxxx → 只要逗号后面那段
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.readAsDataURL(file);
  });
}

/**
 * 传一个文件到已存在的节点。**调用方负责先把节点建出来并置成 uploading。**
 *
 * @param {object} api axios 实例
 * @returns 后端返回的 data（含 url / url_expires_at 等）
 */
export async function uploadFileToNode(api, canvasId, nodeId, file, rawType) {
  const base = `/canvas/${canvasId}/nodes/${nodeId}`;
  if (rawType === 'video') {
    // ⚠️ raw 二进制，不是 base64。理由见文件头。
    return (await api.post(`${base}/upload-video`, file, {
      headers: { 'Content-Type': file.type || 'video/mp4' },
    })).data;
  }
  if (rawType === 'audio') {
    const base64 = await readFileAsBase64(file);
    return (await api.post(`${base}/upload-audio`, {
      base64, mimeType: file.type || 'audio/wav', filename: file.name,
    })).data;
  }
  const compressed = await compressImage(file);
  const base64 = await readFileAsBase64(compressed);
  return (await api.post(`${base}/upload-image`, {
    base64, mimeType: compressed === file ? (file.type || 'image/jpeg') : 'image/jpeg',
  })).data;
}

/**
 * 上传的文件名 → 节点名（8.13 Bug#3）。
 *
 * 症状是同事报的「@引用列表里所有素材都叫『新图片』『新音频』，
 * 引用多个音色参考时全靠波形图猜」。根因不在引用面板——**节点从来就没有名字**：
 * 建节点时一律写死占位名，上传时也没把文件名接过来。
 *
 * 图片/视频还能靠缩略图认，**音频只有一个波形图标，没有名字就真的分不出谁是谁**，
 * 而音色参考恰恰是最需要区分的场景（陈七的声线 vs 叶长歌的声线）。
 *
 * 认不出名字就返回空串，调用方保留占位名——宁可还叫「新音频」，
 * 也别显示一个空白或者 "undefined"。
 */
const NODE_NAME_MAX = 24;

export function fileLabel(filename) {
  const base = String(filename ?? '')
    .split(/[\\/]/).pop()            // 有些浏览器给的是完整路径
    .replace(/\.[^.]+$/, '')         // 去扩展名
    .replace(/[_-]+/g, ' ')          // 下划线/连字符当空格，"chen_qi_v2" → "chen qi v2"
    .replace(/\s+/g, ' ')
    .trim();
  if (!base) return '';
  return base.length > NODE_NAME_MAX ? `${base.slice(0, NODE_NAME_MAX - 1)}…` : base;
}

// 建节点时写死的那几个占位名。替换素材时只有名字还是占位名才覆盖——
// 用户自己改过名的节点，换一次素材就被文件名冲掉，那是丢用户的东西。
const PLACEHOLDER_LABELS = new Set(['新图片', '新音频', '新视频', '新文本', '衍生图片', '衍生音频', '衍生视频']);

export function shouldRenameOnUpload(currentLabel) {
  const name = String(currentLabel ?? '').trim();
  return !name || PLACEHOLDER_LABELS.has(name);
}
